import { useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import differenceInMilliseconds from "date-fns/differenceInMilliseconds";
import throttle from "lodash/throttle";
import { useStorageState } from "../../hooks/useGlobalState";
import { logout } from "../../app/login/duck/actions/login";
import { toast } from "../ToastContainer";
import useUser from "../../hooks/useUser";
import useOnUpdate from "../../hooks/useOnUpdate";
import useOnMount from "../../hooks/useOnMount";
import config from "../../config";

const idleMinutesBeforeLogout = config.idleTimeout || 30;
const idleMinutesBeforeNotice = idleMinutesBeforeLogout - 2;
const idleMs = idleMinutesBeforeLogout * 60 * 1000;
const toastId = "logoutNotice";

function useSessionTimeout() {
  const { isLoggedIn } = useUser();
  const dispatch = useDispatch();
  const [{ lastActiveTime }, setUserActivity] = useStorageState("userActivity");
  const toastShown = useRef();

  const lastActive = lastActiveTime ? new Date(lastActiveTime) : new Date();
  const now = new Date();
  const diff = differenceInMilliseconds(now, lastActive);
  const msToNotice = idleMinutesBeforeNotice * 60 * 1000 - diff;
  const msToLogout = idleMs - diff;

  useEffect(() => {
    if (msToNotice > 0) {
      toastShown.current = false;
      toast.dismiss(toastId);
    }
    if (isLoggedIn) {
      const timeoutId = setTimeout(
        () => {
          if (!toastShown.current) {
            toastShown.current = true;
            toast.error(
              "Inactivity alert: You will be logged out automatically in 2 minutes",
              {
                autoClose: false,
                toastId,
              },
            );
          }
        },
        Math.max(msToNotice, 0),
      );
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isLoggedIn, msToNotice]);

  useEffect(() => {
    if (isLoggedIn) {
      const timeoutId = setTimeout(
        () => {
          const stored = localStorage.getItem("userActivity");
          const freshLastActive = stored
            ? new Date(JSON.parse(stored).lastActiveTime || Date.now())
            : new Date();
          const freshDiff = differenceInMilliseconds(
            new Date(),
            freshLastActive,
          );

          if (freshDiff < idleMs) return;

          dispatch(
            logout("timeout", {
              redirectToLogin: true,
              loginState: {
                loginError:
                  "You were logged out automatically after being inactive for a while.",
              },
            }),
          );
        },
        Math.max(msToLogout, 0),
      );
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isLoggedIn, msToLogout, dispatch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const reset = useCallback(
    throttle(() => {
      setUserActivity({ lastActiveTime: new Date().toISOString() });
    }, 1000),
    [],
  );

  useOnMount(() => {
    reset();
  });

  useOnUpdate(() => {
    if (isLoggedIn) {
      reset();
    } else {
      toastShown.current = false;
      toast.dismiss(toastId);
    }
  }, [isLoggedIn, reset]);

  useEffect(() => {
    document.addEventListener("click", reset);
    document.addEventListener("scroll", reset);
    document.addEventListener("keydown", reset);

    return () => {
      document.removeEventListener("click", reset);
      document.removeEventListener("scroll", reset);
      document.removeEventListener("keydown", reset);
    };
  }, [reset]);
}

const SessionTimeout = () => {
  useSessionTimeout();
  return null;
};

export default SessionTimeout;
